import { Rng } from '../util/rng';

/**
 * Параметры одного канала связи. Каналы независимы (п. 10 ТЗ):
 * команды вверх, телеметрия вниз, видео вниз — деградируют по отдельности.
 */
export interface ChannelConfig {
  /** Базовая односторонняя задержка, мс */
  delay_ms: number;
  /** Джиттер (± мс), даёт в том числе переупорядочивание */
  jitter_ms: number;
  /** Доля потерянных пакетов, 0..1 */
  loss: number;
  /** Доля продублированных пакетов, 0..1 */
  duplicate: number;
  /** Канал полностью оборван */
  down: boolean;
}

export const IDEAL_CHANNEL: ChannelConfig = {
  delay_ms: 0,
  jitter_ms: 0,
  loss: 0,
  duplicate: 0,
  down: false,
};

interface InFlight<T> {
  arrival: number;
  payload: T;
}

/**
 * Однонаправленный канал с задержкой. Ничто в симуляторе не ходит в обход канала:
 * ввод оператора → команды, состояние робота → телеметрия, кадры → видео.
 */
export class Channel<T> {
  private queue: InFlight<T>[] = [];
  private lastDelivery = -Infinity;
  /** Счётчики за сессию — для статистики в UI */
  sent = 0;
  delivered = 0;
  lost = 0;

  constructor(
    public config: ChannelConfig,
    private rng: Rng,
  ) {}

  /** Отправить пакет; now — текущее время симуляции, мс */
  send(payload: T, now: number): void {
    this.sent++;
    if (this.config.down) {
      this.lost++;
      return;
    }
    if (this.rng.chance(this.config.loss)) {
      this.lost++;
      return;
    }
    const jitter = this.rng.sym(this.config.jitter_ms);
    const arrival = now + Math.max(0, this.config.delay_ms + jitter);
    this.queue.push({ arrival, payload });
    if (this.rng.chance(this.config.duplicate)) {
      this.queue.push({ arrival: arrival + this.rng.range(2, 25), payload });
    }
  }

  /** Забрать всё, что доставлено к моменту now. Порядок — по времени прибытия (может отличаться от порядка отправки). */
  receive(now: number): T[] {
    if (this.queue.length === 0) return [];
    this.queue.sort((a, b) => a.arrival - b.arrival);
    const out: T[] = [];
    while (this.queue.length > 0 && this.queue[0].arrival <= now) {
      out.push(this.queue.shift()!.payload);
      this.delivered++;
      this.lastDelivery = now;
    }
    return out;
  }

  /** Сколько мс назад по каналу что-либо приходило */
  silenceMs(now: number): number {
    return now - this.lastDelivery;
  }

  /** Выбросить всё, что ещё летит. Вызывается при входе в RECOVERING (правило 1 п. 10). */
  flush(): number {
    const n = this.queue.length;
    this.queue.length = 0;
    return n;
  }

  get inFlight(): number {
    return this.queue.length;
  }

  get lossRatePct(): number {
    return this.sent === 0 ? 0 : (this.lost / this.sent) * 100;
  }
}
